// T-0156 — the words-not-cron layer for Scheduled Requests.
//
// The backend stores one rule shape: `daily@HH:MM`, a 24-hour time of day in
// **UTC** (`RepeatRule` in src/application/scheduled_requests.rs). The user never
// sees that string, and never sees a clock that isn't their own. So the wire↔human
// conversion happens here, at the API edge, and every sentence the product speaks
// about a Schedule is built from these functions.
//
// Two rules this module exists to keep:
//   1. **Never cron.** No syntax, no "cron", no "job", no "ticket" — a Schedule is
//      described in a sentence a person would say out loud. (Pinned by a test, the
//      way `user_facing_strings_use_the_product_vocabulary` pins the backend's.)
//   2. **Never a fake future tense.** Only the server owns the timer, so "first run
//      today" vs "tomorrow" mirrors the server's own rule (the next occurrence
//      *strictly after* now) rather than guessing at it.
//
// Known v1 edge, deliberately not papered over: the rule is a fixed UTC instant, so
// across a DST boundary its local time shifts by an hour. A timezone-anchored rule
// is the recurrence-editor follow-up (SCHEDULED_REQUESTS_DECISION.md, "SPECCED,
// later"); faking it client-side would make the row lie about when it fires.

import { formatRelativeTime } from '../hooks/useRelativeTime';

/** What one fire costs. The route-pill pricing pattern: see the cost of aliveness
 *  before buying it. */
export const RUN_COST_USD = 0.02;

/** Billing months are quoted at 30 fires — the same round month the pricing page uses. */
const FIRES_PER_MONTH = 30;

/** The one preset the ▾ menu arms without asking anything else. */
export const DEFAULT_SCHEDULE_TIME: LocalTime = { hour: 8, minute: 0 };

/** The `<input type="time">` seed for Custom…, in the field's `HH:MM` format. */
export const DEFAULT_CUSTOM_TIME_VALUE = '08:00';

/** Words of the Request kept in the space-sheet row's name. */
const ROW_WORD_CAP = 6;

/** Max length of the optional "name this schedule" title (T-0214). Mirrors the server's
 *  `SCHEDULE_NAME_CHAR_CAP` in `src/presentation/schedules.rs` — a title is read, not a
 *  place to paste the Request. The composer's input caps at this; the server also rejects
 *  a longer one, so the two never disagree about what a name may be. */
export const SCHEDULE_NAME_MAX = 120;

/** A wall-clock time of day in the *user's* zone. Never UTC — that's `repeat_rule`. */
export interface LocalTime {
  hour: number;
  minute: number;
}

/** The one rule shape v1 speaks: `daily@HH:MM`, fixed-width, UTC. */
const REPEAT_RULE_PATTERN = /^daily@(\d{2}):(\d{2})$/;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * The user's wall-clock time → the `daily@HH:MM` (UTC) the API stores.
 *
 * Goes through a real Date rather than subtracting an offset so zones with a
 * half-hour or quarter-hour offset (India, Nepal, Chatham) land on the right
 * minute, not just the right hour.
 */
export function localTimeToRepeatRule(
  { hour, minute }: LocalTime,
  now: Date = new Date(),
): string {
  const at = new Date(now.getTime());
  at.setHours(hour, minute, 0, 0);
  return `daily@${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}`;
}

/** `daily@HH:MM` (UTC) → the user's wall-clock time. `null` for a rule this
 *  version doesn't understand (a v2 rule read by a v1 client). */
export function repeatRuleToLocalTime(
  rule: string,
  now: Date = new Date(),
): LocalTime | null {
  const match = REPEAT_RULE_PATTERN.exec(rule.trim());
  if (!match) return null;
  const at = new Date(now.getTime());
  at.setUTCHours(Number(match[1]), Number(match[2]), 0, 0);
  return { hour: at.getHours(), minute: at.getMinutes() };
}

/** An ISO instant from the API → the user's wall-clock time. */
export function isoToLocalTime(iso: string): LocalTime | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return { hour: at.getHours(), minute: at.getMinutes() };
}

/** `8:00`, `14:05` — the form every sentence below embeds. */
export function formatLocalTime({ hour, minute }: LocalTime): string {
  return `${hour}:${pad(minute)}`;
}

/** The `<input type="time">` value form, which is zero-padded where we are not. */
export function toTimeInputValue({ hour, minute }: LocalTime): string {
  return `${pad(hour)}:${pad(minute)}`;
}

/** Read an `<input type="time">` value. `null` when the field is empty or partial. */
export function parseTimeInputValue(value: string): LocalTime | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return { hour, minute };
}

/** Which half of the day this hour belongs to, so the sentence reads like speech:
 *  "Every morning at 8:00". */
function daypart(hour: number): string {
  if (hour < 5) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  if (hour < 21) return 'evening';
  return 'night';
}

/** "Every morning at 8:00". */
export function describeCadence(rule: string, now: Date = new Date()): string {
  const local = repeatRuleToLocalTime(rule, now);
  if (!local) return 'Repeats daily';
  return `Every ${daypart(local.hour)} at ${formatLocalTime(local)}`;
}

/**
 * "first run today" | "first run tomorrow".
 *
 * The server arms the first fire at the next occurrence *strictly after* now, so a
 * `daily@08:00` armed at 08:00 or later first runs tomorrow. This mirrors that
 * exactly — it is a claim about the server's timer, so it must not round in our
 * favor.
 */
export function firstRunPhrase(rule: string, now: Date = new Date()): string {
  const local = repeatRuleToLocalTime(rule, now);
  if (!local) return 'first run tomorrow';
  const target = local.hour * 60 + local.minute;
  const current = now.getHours() * 60 + now.getMinutes();
  return target > current ? 'first run today' : 'first run tomorrow';
}

/** "≈ $0.02/run (~$0.60/mo)". */
export function formatCostLine(): string {
  const perRun = RUN_COST_USD.toFixed(2);
  const perMonth = (RUN_COST_USD * FIRES_PER_MONTH).toFixed(2);
  return `≈ $${perRun}/run (~$${perMonth}/mo)`;
}

/**
 * The whole confirm, in words:
 * "Every morning at 8:00 · first run tomorrow · ≈ $0.02/run (~$0.60/mo)"
 */
export function scheduleConfirmSentence(rule: string, now: Date = new Date()): string {
  return `${describeCadence(rule, now)} · ${firstRunPhrase(rule, now)} · ${formatCostLine()}`;
}

/** The row's name: the first few words of the Request itself. A Schedule has no
 *  title field, and asking for one would be a config object — which is the thing
 *  the decision rejected. */
export function scheduleName(requestBody: string): string {
  const words = requestBody.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'Scheduled Request';
  const head = words.slice(0, ROW_WORD_CAP).join(' ');
  return words.length > ROW_WORD_CAP ? `${head}…` : head;
}

/**
 * The whole row, minus its ↻ glyph: "Morning watering check · next 8:00".
 *
 * The row renders the two halves separately (the name may ellipsize; the state
 * may not), and carries this as its title. The tense half is NOT computed here:
 * which tense a Schedule is allowed to speak is the ◐/● law's call, and it lives
 * in `scheduleAliveness.ts` — pass `scheduleStatePhrase(...)` in.
 */
export function scheduleRowSentence(requestBody: string, statePhrase: string): string {
  return `${scheduleName(requestBody)} · ${statePhrase}`;
}

/**
 * The armed Schedule that fires next — the one a space is "about to run", and so
 * the one that speaks for it in the thread header and on the meta-line.
 *
 * A silenced Schedule has no cadence to contribute. `next_fire_at` is an ISO-8601
 * UTC instant, whose lexical order is its chronological order, so the strings
 * compare directly.
 */
export function soonestArmed<T extends { enabled: boolean; next_fire_at: string }>(
  schedules: readonly T[] | undefined,
): T | undefined {
  return (schedules ?? [])
    .filter((s) => s.enabled)
    .sort((a, b) => a.next_fire_at.localeCompare(b.next_fire_at))[0];
}

// ─────────────────────────────────────────────────────────────────────────────
// T-0165 — the CANVAS chip's sentences.
//
// The composer (T-0156) asks "when should this Request repeat?" and answers in the
// future tense, because it is about to arm a server timer. The canvas asks a
// different question — "does this org run on a cadence, and is it keeping to it?" —
// so its sentence carries three tenses at once:
//
//     runs daily 8:00 · last ran 2h ago · next in 22h
//     └ present        └ past            └ future
//
// The future clause is only legal because Tizok's server owns the timer (●). The
// ◐ register — an agent's *claim* that it will wake — is deliberately absent here:
// attested beats (beat.rs counters) never reach the browser, so there is nothing to
// derive a ◐ from, and a self-reported schedule must stay unrepresentable rather
// than be rendered as a promise. When a beat read path lands, ◐ belongs in this file
// beside ●, never as a fallback for a missing ●.
// ─────────────────────────────────────────────────────────────────────────────

/** The fire sweep ticks every 60s, so a fire is only *late* once that window passes.
 *  Mirrors `SWEEP_INTERVAL_DEFAULT_SECS` in `application/scheduled_requests.rs`. */
export const OVERDUE_GRACE_MS = 60_000;

/** What the chip is entitled to say. `overdue` is the amber, past-tense register. */
export type ScheduleTone = 'armed' | 'overdue' | 'paused';

/** The state of the space a Schedule fires into, as far as the chip cares. */
export type SpaceRestState = 'active' | 'sleeping' | 'pending-deletion';

/** The Schedule fields every sentence below reads. */
export interface ScheduleFacts {
  repeat_rule: string;
  next_fire_at: string;
  last_fired_at: string | null;
  enabled: boolean;
}

/** "runs daily 8:00" — present tense, the org's standing cadence. */
export function cadencePhrase(rule: string, now: Date = new Date()): string {
  const local = repeatRuleToLocalTime(rule, now);
  return local ? `runs daily ${formatLocalTime(local)}` : 'repeats daily';
}

/** "last ran 2h ago" — past tense, from the Schedule's own memory. A Schedule that
 *  has never fired says so; it does not borrow the next run's time. */
export function lastRunPhrase(lastFiredAt: string | null, now: Date = new Date()): string {
  if (!lastFiredAt) return 'never run';
  return `last ran ${formatRelativeTime(lastFiredAt, now.getTime())}`;
}

/** "next in 22h" — the ONE future tense in the product, earned by a server-owned
 *  timer. Never called for a paused or overdue Schedule. */
export function nextRunPhrase(nextFireAt: string, now: Date = new Date()): string {
  const ms = new Date(nextFireAt).getTime() - now.getTime();
  if (Number.isNaN(ms)) return 'next run unknown';
  if (ms < 60_000) return 'next in <1m';
  if (ms < 3_600_000) return `next in ${Math.floor(ms / 60_000)}m`;
  return `next in ${Math.floor(ms / 3_600_000)}h`;
}

/** "overdue since 8:00" — the amber confession. Past tense: it states what did not
 *  happen, never when it now will. */
export function overduePhrase(nextFireAt: string): string {
  const due = isoToLocalTime(nextFireAt);
  return due ? `overdue since ${formatLocalTime(due)}` : 'overdue';
}

export function scheduleTone(schedule: ScheduleFacts, now: Date = new Date()): ScheduleTone {
  if (!schedule.enabled) return 'paused';
  const due = new Date(schedule.next_fire_at).getTime();
  if (!Number.isNaN(due) && due < now.getTime() - OVERDUE_GRACE_MS) return 'overdue';
  return 'armed';
}

/**
 * The chip, in one sentence:
 *
 *   armed    "runs daily 8:00 · last ran 2h ago · next in 22h"
 *   overdue  "runs daily 8:00 · last ran 2h ago · overdue since 8:00"
 *   paused   "paused · daily 8:00 · no next run"
 *
 * A paused Schedule names no next run at all. Its stored `next_fire_at` is the stale
 * one from before it was silenced (the server recomputes only on re-arm), so any
 * future tense here would be a promise nothing is keeping.
 */
export function scheduleChipSentence(schedule: ScheduleFacts, now: Date = new Date()): string {
  const cadence = cadencePhrase(schedule.repeat_rule, now);
  const tone = scheduleTone(schedule, now);
  if (tone === 'paused') {
    const local = repeatRuleToLocalTime(schedule.repeat_rule, now);
    const at = local ? `daily ${formatLocalTime(local)}` : 'daily';
    return `paused · ${at} · no next run`;
  }
  const last = lastRunPhrase(schedule.last_fired_at, now);
  const ahead =
    tone === 'overdue'
      ? overduePhrase(schedule.next_fire_at)
      : nextRunPhrase(schedule.next_fire_at, now);
  return `${cadence} · ${last} · ${ahead}`;
}

/**
 * "asleep · wakes 8:00" — the honest rest state. Between fires the org is NOT
 * quietly alive: it sleeps, and the only thing we may say is the named next wake
 * the server will deliver. Never a pulse, never a spinner.
 *
 * `null` whenever we'd have to invent something: the space is awake, the Schedule is
 * paused (nothing will wake it), or the run is already overdue (a wake we can no
 * longer promise).
 */
export function restStatePhrase(
  spaceState: SpaceRestState | undefined,
  schedule: ScheduleFacts,
  now: Date = new Date(),
): string | null {
  if (spaceState !== 'sleeping') return null;
  if (scheduleTone(schedule, now) !== 'armed') return null;
  const wake = isoToLocalTime(schedule.next_fire_at);
  return wake ? `asleep · wakes ${formatLocalTime(wake)}` : null;
}
